import {createApi} from '@reduxjs/toolkit/query/react'
import type {ISuccessMessage} from '~/src/models'
import type {
  AgencyPledgeBreakdownResponse,
  IJobOrder,
  JobOrderCreate,
  JobOrderExtensionReason,
  TJobOrderAddCandidateRequest,
  TJobOrderBatchedResponse,
  TJobOrderPayload,
  TPagedJobOrderInputParams,
} from '~/src/models/jobOrder'
import type {IServiceOrder, ServiceOrderVoidReason} from '~/src/models/serviceOrder'
import {baseQuery} from '../base-query'
import {serviceOrderApi} from './service-order-api'

export interface BatchCandidateEntry {
  candidateId: string
  name?: string
}

export interface BatchCandidateFailure extends BatchCandidateEntry {
  reason: string
}

export interface BatchCandidateResult {
  succeeded: BatchCandidateEntry[]
  failed: BatchCandidateFailure[]
}

export interface SetCandidateRolesResponse {
  jobOrder: IJobOrder
  results: BatchCandidateResult
}

/**
 * The query string of `GET /api/joborder/getJobOrders`. The optional filters go only when set, and
 * `includeHelping` only when true (STFFLG-4184): mspplatform's paging DTO is whitelisted, and a board
 * that does not ask for helping orders sends exactly what it always did.
 */
export function getJobOrdersParams({
  pageNumber,
  pageSize,
  filterBy,
  agencyId,
  companyId,
  regionId,
  siteId,
  departmentId,
  departmentSiteId,
  orderNumberSearch,
  requestDateFrom,
  requestDateTo,
  sortByStartDate,
  includeHelping,
  needsPeople,
}: TPagedJobOrderInputParams): Record<string, string | number | boolean | undefined> {
  return {
    pageNumber,
    pageSize,
    filterBy,
    agencyId,
    companyId,
    ...(regionId && {regionId}),
    ...(siteId && {siteId}),
    ...(departmentId && {departmentId}),
    ...(departmentSiteId && {departmentSiteId}),
    ...(orderNumberSearch && {orderNumberSearch}),
    ...(requestDateFrom && {requestDateFrom}),
    ...(requestDateTo && {requestDateTo}),
    ...(sortByStartDate && {sortByStartDate}),
    ...(includeHelping === true && {includeHelping: true}),
    ...(needsPeople === true && {needsPeople: true}),
  }
}

export const jobOrderApi = createApi({
  reducerPath: 'JobOrderApi',
  tagTypes: ['JobOrder'],
  baseQuery: baseQuery,
  endpoints: (builder) => ({
    getJobOrder: builder.query<IJobOrder, {id: string; agencyId?: string}>({
      providesTags: ['JobOrder'],
      query: ({id, agencyId}) => ({
        url: `/api/joborder`,
        method: 'GET',
        params: {id, agencyId},
      }),
    }),

    // Nothing is using this endpoint in figma but good for future use
    updateFullJobOrder: builder.query<TJobOrderPayload, TPagedJobOrderInputParams>({
      providesTags: ['JobOrder'],
      query: (body) => ({
        url: `/api/joborder`,
        method: 'PUT',
        params: body,
      }),
    }),

    getJobOrders: builder.query<TJobOrderPayload, TPagedJobOrderInputParams>({
      providesTags: ['JobOrder'],
      query: (params) => ({
        url: `/api/joborder/getJobOrders`,
        method: 'GET',
        params: getJobOrdersParams(params),
      }),
    }),

    getJobOrdersByServiceOrderIds: builder.query<
      TJobOrderBatchedResponse[],
      {serviceOrderIds: string[]; agencyId?: string}
    >({
      providesTags: ['JobOrder'],
      query: ({serviceOrderIds, agencyId}) => ({
        url: `/api/joborder/getJobOrdersByServiceOrderIds`,
        method: 'GET',
        params: {serviceOrderIds, agencyId},
      }),
    }),

    getJobOrdersByJobOrderOrderIds: builder.query<IJobOrder[], {ids: string[]; agencyId?: string}>({
      providesTags: ['JobOrder'],
      query: ({ids, agencyId}) => ({
        url: `/api/joborder/getJobOrdersByJobOrderIds`,
        method: 'POST',
        params: {agencyId},
        body: ids,
      }),
    }),

    createJobOrder: builder.mutation<ISuccessMessage, JobOrderCreate>({
      invalidatesTags: ['JobOrder'],
      query: ({agencyId, ...rest}) => ({
        url: `/api/joborder/createJobOrder`,
        method: 'POST',
        body: rest,
        params: {agencyId},
      }),
    }),

    deleteJobOrder: builder.mutation<ISuccessMessage, {id: string; agencyId?: string}>({
      invalidatesTags: ['JobOrder'],
      query: ({id, agencyId}) => ({
        url: `/api/joborder/${id}`,
        method: 'DELETE',
        params: {agencyId},
      }),
    }),

    voidServiceOrderCascade: builder.mutation<
      IServiceOrder,
      {serviceOrderId: string; reason: ServiceOrderVoidReason; note: string; agencyId?: string}
    >({
      invalidatesTags: ['JobOrder'],
      query: ({serviceOrderId, reason, note, agencyId}) => ({
        url: `/api/joborder/service-order/${serviceOrderId}/void`,
        method: 'POST',
        params: {agencyId},
        body: {reason, note},
      }),
      async onQueryStarted(_, {queryFulfilled, dispatch}) {
        await queryFulfilled
        dispatch(serviceOrderApi.util.invalidateTags(['ServiceOrder']))
      },
    }),

    extendJobOrder: builder.mutation<
      IJobOrder,
      {
        id: string
        /** Site calendar date as `YYYY-MM-DD`. An instant here can slip the site-local day. */
        newDate: string
        /** Site-local wall clock as `HH:mm`. The backend rejects instants. */
        newStartTime: string
        reason: JobOrderExtensionReason
        note: string
        agencyId?: string
      }
    >({
      invalidatesTags: ['JobOrder'],
      query: ({id, agencyId, ...body}) => ({
        url: `/api/joborder/${id}/extend`,
        method: 'PUT',
        params: {agencyId},
        body,
      }),
      async onQueryStarted(_, {queryFulfilled, dispatch}) {
        await queryFulfilled
        dispatch(serviceOrderApi.util.invalidateTags(['ServiceOrder']))
      },
    }),

    // This endpoint will be used to update job order candidate list not the general job order update endpoint
    addCandidateToJobOrder: builder.mutation<ISuccessMessage, TJobOrderAddCandidateRequest>({
      invalidatesTags: ['JobOrder'],
      query: ({candidateId, candidateStatus, jobOrderId, notes, agencyId}) => ({
        url: `/api/joborder/addCandidateToJobOrder`,
        method: 'PUT',
        params: {jobOrderId, agencyId},
        body: {candidateId, candidateStatus, notes},
      }),
    }),

    addCandidatesToJobOrder: builder.mutation<
      ISuccessMessage,
      {jobOrderId: string; candidateIds: string[]; agencyId?: string}
    >({
      invalidatesTags: ['JobOrder'],
      query: ({candidateIds, jobOrderId, agencyId}) => ({
        url: `/api/joborder/addCandidatesToJobOrder`,
        method: 'PUT',
        body: {candidateIds, jobOrderId},
        params: {agencyId},
      }),
    }),

    fillJobOrder: builder.mutation<
      IJobOrder,
      {
        jobOrderId: string
        agencyId?: string
        candidateAssignmentIds?: {candidateId: string; assignmentId?: string; employeeId?: string}[]
      }
    >({
      invalidatesTags: ['JobOrder'],
      query: ({candidateAssignmentIds, ...params}) => ({
        url: `/api/joborder/fillJobOrder`,
        method: 'PUT',
        params,
        body: candidateAssignmentIds?.length ? {candidateAssignmentIds} : undefined,
      }),
    }),

    removeCandidateFromJobOrder: builder.mutation<
      ISuccessMessage,
      {jobOrderId: string; candidateId: string; agencyId?: string}
    >({
      invalidatesTags: ['JobOrder'],
      query: (params) => ({
        url: `/api/joborder/removeCandidateFromJobOrder`,
        method: 'PUT',
        params: params,
      }),
    }),

    declineCandidateFromJobOrder: builder.mutation<
      ISuccessMessage,
      {jobOrderId: string; candidateId: string; notes: string; agencyId?: string}
    >({
      invalidatesTags: ['JobOrder'],
      query: ({candidateId, jobOrderId, notes, agencyId}) => ({
        url: `/api/joborder/declineCandidateForJobOrder`,
        method: 'PUT',
        params: {jobOrderId, agencyId},
        body: {candidateId, notes},
      }),
    }),

    skipCandidateFromJobOrder: builder.mutation<
      ISuccessMessage,
      {jobOrderId: string; candidateId: string; notes: string; agencyId?: string}
    >({
      invalidatesTags: ['JobOrder'],
      query: ({candidateId, jobOrderId, notes, agencyId}) => ({
        url: `/api/joborder/skipCandidateForJobOrder`,
        method: 'PUT',
        params: {jobOrderId, agencyId},
        body: {candidateId, notes, candidateStatus: 'Skipped'},
      }),
    }),

    updateCandidateEndDate: builder.mutation<
      ISuccessMessage,
      {agencyId?: string; id: string; candidateId: string; reason: string; endDate: Date}
    >({
      invalidatesTags: ['JobOrder'],
      query: ({agencyId, ...rest}) => ({
        url: `/api/joborder/updateCandidateEndDate`,
        method: 'PUT',
        params: {agencyId},
        body: {...rest},
      }),
    }),

    // New Agency Pledge Breakdown endpoint
    getAgencyPledgeBreakdown: builder.query<AgencyPledgeBreakdownResponse, {jobOrderId: string}>({
      providesTags: ['JobOrder'],
      query: ({jobOrderId}) => ({
        url: `/api/job-order/${jobOrderId}/agency-pledge-breakdown`,
        method: 'GET',
      }),
    }),

    // --- Backup Order endpoints ---
    // All use invalidatesTags: ['JobOrder'] to trigger the RTK cache → refetch chain
    // (see dependency map in plan: mutation → invalidate → useGetJobOrderQuery refetches → derived state recomputes)

    getBackupGroups: builder.query<IJobOrder[], {parentJobOrderId: string; agencyId?: string}>({
      providesTags: ['JobOrder'],
      query: ({parentJobOrderId, agencyId}) => ({
        url: `/api/joborder/${parentJobOrderId}/backups`,
        method: 'GET',
        params: {agencyId},
      }),
    }),

    getBackupGroup: builder.query<
      IJobOrder,
      {parentJobOrderId: string; groupId: string; agencyId?: string}
    >({
      providesTags: ['JobOrder'],
      query: ({parentJobOrderId, groupId, agencyId}) => ({
        url: `/api/joborder/${parentJobOrderId}/backups/${groupId}`,
        method: 'GET',
        params: {agencyId},
      }),
    }),

    createBackupGroup: builder.mutation<
      IJobOrder,
      {
        parentJobOrderId: string
        backupCount: number
        agencyDistribution: {agencyId: string; requested: number}[]
        agencyId?: string
      }
    >({
      invalidatesTags: ['JobOrder'],
      query: ({parentJobOrderId, agencyId, ...body}) => ({
        url: `/api/joborder/${parentJobOrderId}/backups`,
        method: 'POST',
        params: {agencyId},
        body,
      }),
    }),

    setCandidateRoles: builder.mutation<
      SetCandidateRolesResponse,
      {
        jobOrderId: string
        backupGroupId: string
        updates?: {candidateId: string; role: 'primary' | 'backup'}[]
        newCandidates?: {candidateId: string; agencyId: string; role: 'primary' | 'backup'}[]
        agencyId?: string
      }
    >({
      invalidatesTags: ['JobOrder'],
      query: ({jobOrderId, agencyId, ...body}) => ({
        url: `/api/joborder/${jobOrderId}/setCandidateRoles`,
        method: 'PUT',
        params: {agencyId},
        body,
      }),
    }),

    addBackupCandidates: builder.mutation<
      SetCandidateRolesResponse,
      {
        groupId: string
        candidates: {candidateId: string; agencyId: string}[]
        agencyId?: string
      }
    >({
      invalidatesTags: ['JobOrder'],
      query: ({groupId, agencyId, ...body}) => ({
        url: `/api/joborder/${groupId}/addBackupCandidates`,
        method: 'PUT',
        params: {agencyId},
        body,
      }),
    }),

    submitBackupGroup: builder.mutation<
      IJobOrder,
      {
        groupId: string
        agencyId?: string
        candidateAssignmentIds?: {candidateId: string; assignmentId?: string; employeeId?: string}[]
      }
    >({
      invalidatesTags: ['JobOrder'],
      query: ({groupId, agencyId, candidateAssignmentIds}) => ({
        url: `/api/joborder/${groupId}/submitBackup`,
        method: 'PUT',
        params: {agencyId},
        body: candidateAssignmentIds?.length ? {candidateAssignmentIds} : undefined,
      }),
    }),

    saveCandidateDraftIds: builder.mutation<
      IJobOrder,
      {
        jobOrderId: string
        candidateId: string
        agencyId?: string
        draftAssignmentId?: string | null
        draftEmployeeId?: string | null
      }
    >({
      invalidatesTags: ['JobOrder'],
      query: ({jobOrderId, candidateId, agencyId, ...body}) => ({
        url: `/api/joborder/${jobOrderId}/candidates/${candidateId}/ids`,
        method: 'PATCH',
        params: {agencyId},
        body,
      }),
    }),

    patchJobOrder: builder.mutation<ISuccessMessage, {id: string; payRate: string}>({
      invalidatesTags: ['JobOrder'],
      query: ({id, payRate}) => ({
        url: `/api/joborder/${id}`,
        method: 'PATCH',
        body: {payRate},
      }),
      async onQueryStarted(_, {queryFulfilled, dispatch}) {
        await queryFulfilled
        dispatch(serviceOrderApi.util.invalidateTags(['ServiceOrder']))
      },
    }),
  }),
})

export const {
  useGetJobOrderQuery,
  useUpdateFullJobOrderQuery,
  useGetJobOrdersQuery,
  useGetJobOrdersByServiceOrderIdsQuery,
  useLazyGetJobOrdersByJobOrderOrderIdsQuery,
  useCreateJobOrderMutation,
  useVoidServiceOrderCascadeMutation,
  useExtendJobOrderMutation,
  useAddCandidateToJobOrderMutation,
  useAddCandidatesToJobOrderMutation,
  useFillJobOrderMutation,
  useRemoveCandidateFromJobOrderMutation,
  useDeclineCandidateFromJobOrderMutation,
  useSkipCandidateFromJobOrderMutation,
  useUpdateCandidateEndDateMutation,
  // New Agency Pledge Breakdown hook
  useGetAgencyPledgeBreakdownQuery,
  // Backup order hooks
  useGetBackupGroupsQuery,
  useGetBackupGroupQuery,
  useLazyGetBackupGroupQuery,
  useCreateBackupGroupMutation,
  useSetCandidateRolesMutation,
  useAddBackupCandidatesMutation,
  useSubmitBackupGroupMutation,
  useSaveCandidateDraftIdsMutation,
  usePatchJobOrderMutation,
} = jobOrderApi
